///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { GtthNotificationsService } from '../../project-view-panel/logical-topology/engine/notifications-service';
import { GtthProjectStateService } from '../../project-view-panel/logical-topology/engine/project-state.service';
import { ExportLinkConfig } from '../logical-topology/export-link-config.model';
import { LoaderIds, PreloaderService } from '../preloader.service';
import { GtthLayeredBomSummaryDTO } from '../../generated/shared-types.model';
import { finalize } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class GtthExportService {
    private readonly FILE_NAME_HEADER = 'planner-file-name';
    private readonly reportsUrl = 'api/reports';
    private readonly ncfUrl = 'api/ncf';
    private readonly topologyUrl = 'api/topology';
    private readonly systemReportsUrl = 'api/system-reports';
    private readonly projectFilesUrl = 'api/project-files';

    constructor(
        private readonly router: Router,
        private readonly http: HttpClient,
        private readonly notificationService: GtthNotificationsService,
        private readonly preloaderService: PreloaderService,
        private readonly projectStateService: GtthProjectStateService
    ) {}

    uploadImportFileTemplate(): void {
        this.processGetRequestDownload(`${this.topologyUrl}/importTemplate/${this.projectStateService.getProjectId()}`);
    }

    exportKmlFile(): void {
        this.processGetRequestDownload(`${this.topologyUrl}/exportKml/${this.projectStateService.getProjectId()}`, true, (error: any) =>
            this.navigateToAnIpReport(error)
        );
    }

    exportSites(layerIndex: number, fileExtension: string, includeLoses: boolean): void {
        this.processGetRequestDownload(
            `${this.topologyUrl}/exportSites/${this.projectStateService.getProjectId()}/${layerIndex}/${fileExtension}?includeLoses=${includeLoses}`
        );
    }

    exportConstraintComplianceReport(layerIndex: number): void {
        this.processGetRequestDownload(
            `${this.reportsUrl}/${this.projectStateService.getProjectId()}/constraint-compliance-file/${layerIndex}`
        );
    }

    exportConnectedSitesReport(layerIndex: number): void {
        this.processGetRequestDownload(`${this.reportsUrl}/${this.projectStateService.getProjectId()}/connected-sites-file/${layerIndex}`);
    }

    exportP2pReport(layerIndex: number): void {
        this.processGetRequestDownload(`${this.reportsUrl}/${this.projectStateService.getProjectId()}/P2P-file/${layerIndex}`);
    }

    exportP2mpReport(layerIndex: number): void {
        this.processGetRequestDownload(`${this.reportsUrl}/${this.projectStateService.getProjectId()}/P2MP-file/${layerIndex}`);
    }

    exportAllReports(layerIndex: number): void {
        this.processGetRequestDownload(`${this.reportsUrl}/${this.projectStateService.getProjectId()}/all/${layerIndex}`);
    }

    exportLinkConfiguration(configObject: ExportLinkConfig, layerIndex: number): void {
        this.processPutRequestDownload(
            `${this.reportsUrl}/${this.projectStateService.getProjectId()}/export-link-config-file/${layerIndex}`,
            configObject,
            (error: any) => this.navigateToAnIpReport(error)
        );
    }

    exportLinkSummary(configObject: ExportLinkConfig): void {
        this.processPutRequestDownload(
            `${this.reportsUrl}/${this.projectStateService.getProjectId()}/export-link-summary-file`,
            configObject,
            (error: any) => this.navigateToAnIpReport(error)
        );
    }

    exportBomConfigToExcel(layerIndex: number): void {
        this.processGetRequestDownload(`${this.reportsUrl}/${this.projectStateService.getProjectId()}/bom-file/${layerIndex}`);
    }

    exportBomSummaryToExcel(bomConfig: GtthLayeredBomSummaryDTO, layerIndex: number, maskId: LoaderIds): void {
        this.processPutRequestDownload(
            `${this.reportsUrl}/${this.projectStateService.getProjectId()}/bom-summary-file/${layerIndex}`,
            bomConfig,
            undefined,
            maskId
        );
    }

    exportBomSummaryInSalesForceFormat(bomConfig: GtthLayeredBomSummaryDTO, maskId: LoaderIds): void {
        this.processPutRequestDownload(
            `${this.reportsUrl}/${this.projectStateService.getProjectId()}/bom-summary-salesforce-file`,
            bomConfig,
            undefined,
            maskId
        );
    }

    exportRunnerJsonConfiguration(): void {
        this.processGetRequestDownload(`${this.ncfUrl}/${this.projectStateService.getProjectId()}`, true, (error: any) =>
            this.navigateToAnIpReport(error)
        );
    }

    exportProjectFilesArchive(projectId: number) {
        this.processGetRequestDownload(`${this.projectFilesUrl}/archive/${projectId}`, false);
    }

    exportProjectInterchangeArchive(projectId: number) {
        this.processGetRequestDownload(`${this.projectFilesUrl}/interchange/${projectId}`, true);
    }

    downloadStatisticReport(): void {
        this.processGetRequestDownload(`${this.systemReportsUrl}/companies-overview-file`, false);
    }

    private navigateToAnIpReport(error: any) {
        if (error.message.includes('assignedNameOrIpAddressIsNotUnique')) {
            this.router.navigate([{ outlets: { popup: `project-view/an-ip-report` } }], { replaceUrl: true });
        }
    }

    private handleError(errorBlob: any, errorCallback?: (error: any) => void) {
        const notificationService = this.notificationService;
        const fileReader = new FileReader();

        fileReader.onload = function () {
            const error = JSON.parse(this.result.toString());
            notificationService.showHttpError(error);
            if (errorCallback) {
                errorCallback(error);
            }
        };

        fileReader.readAsText(errorBlob.error);
    }

    private processGetRequestDownload(
        uri: string,
        checkIsProjectOpen = true,
        errorCallback?: (error: any) => void,
        fileName?: string
    ): void {
        if (checkIsProjectOpen && !this.projectStateService.projectIsOpened()) {
            return;
        }

        this.preloaderService.show();

        this.http
            .get(uri, { responseType: 'blob', observe: 'response' })
            .pipe(
                finalize(() => {
                    this.preloaderService.hide();
                })
            )
            .subscribe({
                next: (response) => {
                    this.downloadFile(response, fileName);
                },
                error: (error) => {
                    this.handleError(error, errorCallback);
                }
            });
    }

    private processPutRequestDownload(uri: string, config: any, errorCallback?: (error: any) => void, maskId?: LoaderIds): void {
        if (!this.projectStateService.projectIsOpened()) {
            return;
        }

        if (maskId) {
            this.preloaderService.show(maskId);
        } else {
            this.preloaderService.show();
        }

        this.http
            .put(uri, config, { responseType: 'blob', observe: 'response' })
            .pipe(
                finalize(() => {
                    if (maskId) {
                        this.preloaderService.hide(maskId);
                    } else {
                        this.preloaderService.hide();
                    }
                })
            )
            .subscribe({
                next: (response) => {
                    this.downloadFile(response);
                },
                error: (error) => {
                    this.handleError(error, errorCallback);
                }
            });
    }

    private downloadFile(response: HttpResponse<Blob>, fileName?: string) {
        const filename = fileName || response.headers.get(this.FILE_NAME_HEADER);
        const blob = response.body;

        const URL = window.URL || window.webkitURL;
        const downloadUrl = URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();

        setTimeout(() => {
            URL.revokeObjectURL(downloadUrl);
        }, 100);
    }
}
